// frontend/TwitterAnalytics/modules/CommentAnalysis.jsx
/**
 * Module 3: Comment Analysis & Reviews
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, IconButton, Avatar,
  CircularProgress, Tooltip, Rating, LinearProgress, Alert, Divider
} from '@mui/material';
import {
  Psychology as AIIcon,
  SentimentVerySatisfied as PositiveIcon,
  SentimentVeryDissatisfied as NegativeIcon,
  SentimentNeutral as NeutralIcon,
  Help as QuestionIcon,
  Build as FeatureIcon,
  Support as SupportIcon,
  Favorite as AppreciationIcon,
  Warning as ComplaintIcon,
  Block as SpamIcon,
  ModelTraining as TrainIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from "../../../../utils/axiosInstance";

// Sentiment Overview Card
const SentimentCard = ({ sentiment, count, total, icon, color }) => {
  const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
  return (
    <Paper sx={{ p: 2, textAlign: 'center' }}>
      <Box display="flex" justifyContent="center" mb={1}>
        {icon}
      </Box>
      <Typography variant="h4">{count}</Typography>
      <Typography variant="body2" color="textSecondary">{sentiment}</Typography>
      <LinearProgress 
        variant="determinate" 
        value={parseFloat(percentage)} 
        color={color}
        sx={{ mt: 1, height: 8, borderRadius: 4 }}
      />
      <Typography variant="caption">{percentage}%</Typography>
    </Paper>
  );
};

// Intent Badge
const IntentBadge = ({ intent }) => {
  const config = {
    support_request: { icon: <SupportIcon fontSize="small" />, color: 'info' },
    feature_request: { icon: <FeatureIcon fontSize="small" />, color: 'secondary' },
    appreciation: { icon: <AppreciationIcon fontSize="small" />, color: 'success' },
    complaint: { icon: <ComplaintIcon fontSize="small" />, color: 'error' },
    question: { icon: <QuestionIcon fontSize="small" />, color: 'primary' },
    feedback: { icon: <AIIcon fontSize="small" />, color: 'default' },
    spam: { icon: <SpamIcon fontSize="small" />, color: 'warning' },
    troll: { icon: <SpamIcon fontSize="small" />, color: 'error' }
  };
  const cfg = config[intent] || { icon: null, color: 'default' };
  return <Chip icon={cfg.icon} label={intent?.replace('_', ' ')} size="small" color={cfg.color} />;
};

const CommentAnalysis = ({ apiTier, onShowSnackbar }) => {
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  
  // Posts for analysis
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Comments browser
  const [comments, setComments] = useState([]);
  const [commentFilters, setCommentFilters] = useState({
    sentiment: '',
    intent: '',
    post_id: ''
  });
  const [commentsPagination, setCommentsPagination] = useState({
    page: 1, per_page: 20, total: 0
  });

  useEffect(() => {
    fetchOverview();
    fetchPosts();
  }, []);

  useEffect(() => {
    fetchComments();
  }, [commentFilters, commentsPagination.page]);

  const fetchOverview = async () => {
    try {
      const res = await axiosInstance.get('/api/twitter/sentiment/overview');
      if (res.data.success) {
        setOverview(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch overview');
    }
  };

  const fetchPosts = async () => {
    try {
      const res = await axiosInstance.get('/api/twitter/posts', {
        params: { per_page: 50 }
      });
      if (res.data.success) {
        setPosts(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch posts');
    }
  };

  const fetchComments = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/twitter/comments', {
        params: {
          ...commentFilters,
          page: commentsPagination.page,
          per_page: commentsPagination.per_page
        }
      });
      const response = res.data;
      if (response.success) {
        setComments(response.data);
        setCommentsPagination(prev => ({ ...prev, total: response.pagination.total }));
      }
    } catch (err) {
      onShowSnackbar('Failed to fetch comments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzePost = async (postId) => {
    setAnalyzing(true);
    setSelectedPost(postId);
    try {
      const res = await axiosInstance.post(`/api/twitter/posts/${postId}/analyze`);
      const response = res.data;
      if (response.success) {
        setAnalysisResult(response.data);
        onShowSnackbar('Analysis complete!', 'success');
        fetchOverview();
      } else {
        onShowSnackbar(response.message || 'Analysis failed', 'error');
      }
    } catch (err) {
      onShowSnackbar(err.message || 'Analysis failed', 'error');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFetchComments = async (postId) => {
    try {
      const res = await axiosInstance.post(`/api/twitter/posts/${postId}/comments/fetch`);
      const response = res.data;
      if (response.success) {
        onShowSnackbar(`Fetched ${response.new_comments} new comments`, 'success');
      } else {
        onShowSnackbar(response.message || 'Failed to fetch', 'error');
      }
    } catch (err) {
      onShowSnackbar(err.message || 'Failed to fetch comments', 'error');
    }
  };

  const handleTrainPost = async (postId) => {
    try {
      const res = await axiosInstance.post(`/api/twitter/posts/${postId}/train`);
      const response = res.data;
      if (response.success) {
        onShowSnackbar('Trained successfully!', 'success');
      } else {
        onShowSnackbar(response.message || 'Training failed', 'error');
      }
    } catch (err) {
      onShowSnackbar('Training failed', 'error');
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive': return <PositiveIcon color="success" />;
      case 'negative': return <NegativeIcon color="error" />;
      default: return <NeutralIcon color="action" />;
    }
  };

  return (
    <Box>
      {/* Sentiment Overview */}
      {overview && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <SentimentCard
              sentiment="Positive"
              count={overview.sentiment_distribution.positive || 0}
              total={overview.total_comments}
              icon={<PositiveIcon sx={{ fontSize: 40, color: 'success.main' }} />}
              color="success"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <SentimentCard
              sentiment="Neutral"
              count={overview.sentiment_distribution.neutral || 0}
              total={overview.total_comments}
              icon={<NeutralIcon sx={{ fontSize: 40, color: 'grey.500' }} />}
              color="inherit"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <SentimentCard
              sentiment="Negative"
              count={overview.sentiment_distribution.negative || 0}
              total={overview.total_comments}
              icon={<NegativeIcon sx={{ fontSize: 40, color: 'error.main' }} />}
              color="error"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{overview.analyzed_posts}</Typography>
              <Typography variant="body2" color="textSecondary">Posts Analyzed</Typography>
              <Typography variant="caption">{overview.total_comments} total comments</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Intent Distribution */}
      {overview?.intent_distribution && Object.keys(overview.intent_distribution).length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Intent Distribution</Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {Object.entries(overview.intent_distribution).map(([intent, count]) => (
              <Chip 
                key={intent}
                label={`${intent.replace('_', ' ')}: ${count}`}
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Posts for Analysis */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Analyze Posts</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Select a post to fetch comments and run AI analysis
        </Typography>
        
        {apiTier === 'free' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Fetching comments requires Twitter Basic tier ($100/mo). Upgrade to analyze comments.
          </Alert>
        )}

        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Post</TableCell>
                <TableCell align="center">Replies</TableCell>
                <TableCell align="center">Rating</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.map((post) => (
                <TableRow key={post.id} hover>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" noWrap>
                      {post.text?.substring(0, 80)}...
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {post.posted_at && format(new Date(post.posted_at), 'MMM d, yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{post.reply_count}</TableCell>
                  <TableCell align="center">
                    {post.satisfaction_score ? (
                      <Rating value={post.satisfaction_score} precision={0.5} readOnly size="small" />
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5} justifyContent="center">
                      {post.comments_fetched && <Chip label="Fetched" size="small" color="info" />}
                      {post.is_analyzed && <Chip label="Analyzed" size="small" color="success" />}
                      {post.is_trained && <Chip label="Trained" size="small" color="primary" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Fetch Comments">
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => handleFetchComments(post.id)}
                            disabled={apiTier === 'free'}
                          >
                            <RefreshIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="AI Analysis">
                        <span>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleAnalyzePost(post.id)}
                            disabled={analyzing || !post.comments_fetched}
                          >
                            {analyzing && selectedPost === post.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <AIIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Train Chatbot">
                        <span>
                          <IconButton 
                            size="small" 
                            color="secondary"
                            onClick={() => handleTrainPost(post.id)}
                            disabled={!post.is_analyzed}
                          >
                            <TrainIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Analysis Result */}
      {analysisResult && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Analysis Result</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Summary</Typography>
              <Typography variant="body2">{analysisResult.analysis?.summary}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2">Star Rating</Typography>
              <Rating value={analysisResult.analysis?.star_rating || 0} precision={0.5} readOnly />
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2">Comments Analyzed</Typography>
              <Typography variant="h5">{analysisResult.total_comments}</Typography>
            </Grid>
            
            {analysisResult.analysis?.key_themes?.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2">Key Themes</Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {analysisResult.analysis.key_themes.map((theme, idx) => (
                    <Chip key={idx} label={theme} size="small" />
                  ))}
                </Box>
              </Grid>
            )}

            {analysisResult.analysis?.urgent_issues?.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="error">Urgent Issues</Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {analysisResult.analysis.urgent_issues.map((issue, idx) => (
                    <Chip key={idx} label={issue} size="small" color="error" />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Comments Browser */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Browse Comments</Typography>
        
        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sentiment</InputLabel>
              <Select
                value={commentFilters.sentiment}
                label="Sentiment"
                onChange={(e) => setCommentFilters(prev => ({ ...prev, sentiment: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="positive">Positive</MenuItem>
                <MenuItem value="neutral">Neutral</MenuItem>
                <MenuItem value="negative">Negative</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Intent</InputLabel>
              <Select
                value={commentFilters.intent}
                label="Intent"
                onChange={(e) => setCommentFilters(prev => ({ ...prev, intent: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="support_request">Support Request</MenuItem>
                <MenuItem value="feature_request">Feature Request</MenuItem>
                <MenuItem value="appreciation">Appreciation</MenuItem>
                <MenuItem value="complaint">Complaint</MenuItem>
                <MenuItem value="question">Question</MenuItem>
                <MenuItem value="feedback">Feedback</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell align="center">Sentiment</TableCell>
                <TableCell align="center">Intent</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center"><CircularProgress /></TableCell>
                </TableRow>
              ) : comments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">No comments found</TableCell>
                </TableRow>
              ) : (
                comments.map((comment) => (
                  <TableRow key={comment.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={comment.author_profile_image} sx={{ width: 24, height: 24 }}>
                          {comment.author_username?.[0]}
                        </Avatar>
                        <Typography variant="caption">@{comment.author_username}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 400 }}>
                      <Typography variant="body2">{comment.text}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {getSentimentIcon(comment.sentiment)}
                    </TableCell>
                    <TableCell align="center">
                      {comment.intent && <IntentBadge intent={comment.intent} />}
                    </TableCell>
                    <TableCell>
                      {comment.commented_at && format(new Date(comment.commented_at), 'MMM d')}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={commentsPagination.total}
            page={commentsPagination.page - 1}
            onPageChange={(e, page) => setCommentsPagination(prev => ({ ...prev, page: page + 1 }))}
            rowsPerPage={commentsPagination.per_page}
            onRowsPerPageChange={(e) => setCommentsPagination(prev => ({ 
              ...prev, 
              per_page: parseInt(e.target.value), 
              page: 1 
            }))}
          />
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default CommentAnalysis;